import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import * as workOrderAttachmentService from "../../services/workOrderAttachmentService";
import {
  WorkOrderAttachmentItem,
  WorkOrderAttachmentResponse,
  WorkOrderAttachmentCreatePayload,
} from "../../services/workOrderAttachmentService";

interface WorkOrderAttachmentState {
  items: WorkOrderAttachmentItem[];
  loading: boolean;
  error: string | null;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

const initialState: WorkOrderAttachmentState = {
  items: [],
  loading: false,
  error: null,
  pageNumber: 1,
  pageSize: 100,
  totalCount: 0,
  totalPages: 0,
};

// Async thunk to fetch work order attachments
export const fetchWorkOrderAttachments = createAsyncThunk(
  "workOrderAttachments/fetchWorkOrderAttachments",
  async ({
    workOrderId,
    pageNumber = 1,
    pageSize = 100,
  }: {
    workOrderId: string;
    pageNumber?: number;
    pageSize?: number;
  }) => {
    const response: WorkOrderAttachmentResponse =
      await workOrderAttachmentService.getWorkOrderAttachments(
        workOrderId,
        pageNumber,
        pageSize
      );
    return response;
  }
);

// Async thunk to upload a new attachment
export const uploadWorkOrderAttachment = createAsyncThunk(
  "workOrderAttachments/uploadWorkOrderAttachment",
  async (payload: WorkOrderAttachmentCreatePayload) => {
    const response = await workOrderAttachmentService.uploadWorkOrderAttachment(payload);
    return response;
  }
);

// Async thunk to delete an attachment
export const deleteWorkOrderAttachment = createAsyncThunk(
  "workOrderAttachments/deleteWorkOrderAttachment",
  async (attachmentId: string) => {
    await workOrderAttachmentService.deleteWorkOrderAttachment(attachmentId);
    return attachmentId;
  }
);

const workOrderAttachmentSlice = createSlice({
  name: "WorkOrderAttachments",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearAttachments(state) {
      state.items = [];
      state.totalCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch attachments
      .addCase(fetchWorkOrderAttachments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkOrderAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload && action.payload.items && Array.isArray(action.payload.items)) {
          state.items = action.payload.items.filter((item) => !item.isDeleted);
          state.pageNumber = action.payload.pageNumber || 1;
          state.pageSize = action.payload.pageSize || 100;
          state.totalCount = action.payload.totalCount || 0;
          state.totalPages = action.payload.totalPages || 0;
        } else {
          state.items = [];
          state.pageNumber = action.payload?.pageNumber || 1;
          state.pageSize = action.payload?.pageSize || 100;
          state.totalCount = action.payload?.totalCount || 0;
          state.totalPages = action.payload?.totalPages || 0;
        }
      })
      .addCase(fetchWorkOrderAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch attachments";
      })
      // Upload attachment
      .addCase(uploadWorkOrderAttachment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadWorkOrderAttachment.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(uploadWorkOrderAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to upload attachment";
      })
      // Delete attachment
      .addCase(deleteWorkOrderAttachment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWorkOrderAttachment.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.totalCount -= 1;
      })
      .addCase(deleteWorkOrderAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete attachment";
      });
  },
});

export const { clearError, clearAttachments } = workOrderAttachmentSlice.actions;
export default workOrderAttachmentSlice.reducer;

// Selectors
export const selectWorkOrderAttachmentList = (state: any) =>
  state.WorkOrderAttachments?.items || [];
export const selectWorkOrderAttachmentLoading = (state: any) =>
  state.WorkOrderAttachments?.loading || false;
export const selectWorkOrderAttachmentError = (state: any) =>
  state.WorkOrderAttachments?.error || null;
export const selectWorkOrderAttachmentTotalCount = (state: any) =>
  state.WorkOrderAttachments?.totalCount || 0;

// Memoized selector to filter attachments by workOrderId
const selectAllAttachments = (state: any) => state.WorkOrderAttachments?.items || [];

export const selectWorkOrderAttachmentsByWorkOrderId = createSelector(
  [selectAllAttachments, (_state: any, workOrderId: string) => workOrderId],
  (items: WorkOrderAttachmentItem[], workOrderId: string) => {
    return items.filter((item: WorkOrderAttachmentItem) => item.workOrderId === workOrderId);
  }
);


